import React, { useState, useEffect } from 'react';
import { 
  Target, 
  TrendingUp,
  Users,
  Zap,
  Brain,
  CheckCircle2,
  Clock,
  DollarSign,
  Phone,
  Mail,
  MapPin,
  ThermometerSun,
  Star,
  ArrowRight,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { supabase } from '../lib/supabase';

interface Lead {
  id: string;
  contact_name: string;
  contact_email: string | null;
  contact_phone: string | null;
  company_name: string | null;
  score: number;
  score_grade: string;
  score_breakdown: Record<string, number>;
  recommended_actions: string[];
  source: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}

interface ScoringModel {
  id: string;
  model_name: string;
  description: string | null;
  is_active: boolean;
  scoring_rules: Record<string, any>;
}

export default function LeadScoringPage() {
  const [activeTab, setActiveTab] = useState<'leads' | 'model'>('leads');
  const [leads, setLeads] = useState<Lead[]>([]);
  const [model, setModel] = useState<ScoringModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load leads
      const { data: leadsData, error: leadsError } = await supabase
        .from('marketing_scored_leads')
        .select('*')
        .eq('company_id', 'halesac')
        .order('score', { ascending: false });

      if (leadsError) throw leadsError;

      // Load active model
      const { data: modelData, error: modelError } = await supabase
        .from('marketing_lead_scoring_models')
        .select('*')
        .eq('company_id', 'halesac')
        .eq('is_active', true)
        .single();

      if (modelError && modelError.code !== 'PGRST116') throw modelError;

      setLeads(leadsData || []);
      setModel(modelData);
    } catch (err: any) {
      console.error('Error loading lead scoring data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateLeadStatus = async (leadId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('marketing_scored_leads')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', leadId);

      if (error) throw error;

      // Reload data
      await loadData();
    } catch (err: any) {
      console.error('Error updating lead status:', err);
      alert('Failed to update lead status: ' + err.message);
    }
  };

  const calculateMetrics = () => {
    const total = leads.length;
    const hotLeads = leads.filter(l => l.score_grade === 'hot').length;
    const warmLeads = leads.filter(l => l.score_grade === 'warm').length;
    const coldLeads = leads.filter(l => l.score_grade === 'cold').length;
    const avgScore = total > 0 ? (leads.reduce((sum, l) => sum + l.score, 0) / total).toFixed(1) : '0';
    
    return [
      { label: 'Total Leads', value: total.toString(), change: `${hotLeads} hot, ${warmLeads} warm` },
      { label: 'Hot Leads', value: hotLeads.toString(), change: `${((hotLeads/total)*100 || 0).toFixed(0)}% of total` },
      { label: 'Average Score', value: avgScore, change: 'Across all leads' },
      { label: 'Warm Leads', value: warmLeads.toString(), change: `${((warmLeads/total)*100 || 0).toFixed(0)}% of total` },
      { label: 'Cold Leads', value: coldLeads.toString(), change: `${((coldLeads/total)*100 || 0).toFixed(0)}% of total` },
      { label: 'Model Active', value: model?.is_active ? 'Yes' : 'No', change: model?.model_name || 'No model' }
    ];
  };

  const getPriorityColor = (grade: string) => {
    switch (grade) {
      case 'hot': return 'text-red-600 bg-red-50 border-red-200';
      case 'warm': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'lukewarm': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'cold': return 'text-blue-600 bg-blue-50 border-blue-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getPriorityIcon = (grade: string) => {
    switch (grade) {
      case 'hot': return <ThermometerSun className="w-4 h-4" />;
      case 'warm': return <Target className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      new: 'bg-blue-100 text-blue-700',
      contacted: 'bg-purple-100 text-purple-700',
      qualified: 'bg-green-100 text-green-700',
      converted: 'bg-emerald-100 text-emerald-700',
      lost: 'bg-gray-100 text-gray-700'
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${colors[status] || colors.new}`}>
        {status.toUpperCase()}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading lead scoring data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 max-w-2xl mx-auto">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-900 mb-2">Failed to Load Lead Scoring Data</h3>
              <p className="text-red-700 mb-4">{error}</p>
              <button
                onClick={loadData}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl">
            <Target className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Lead Scoring</h1>
            <p className="text-gray-600 mt-1">Predictive lead prioritization and conversion tracking</p>
          </div>
        </div>
      </div>

      {model && (
        <div className="mb-6 p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border-2 border-blue-200">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Brain className="w-6 h-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-2">{model.model_name}</h3>
              {model.description && (
                <p className="text-sm text-gray-600 mb-3">{model.description}</p>
              )}
              <div className="grid grid-cols-5 gap-4">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-gray-600">Model Active</span>
                </div>
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-yellow-500" />
                  <span className="text-sm text-gray-600">Real-time Scoring</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-blue-500" />
                  <span className="text-sm text-gray-600">{leads.length} Total Leads</span>
                </div>
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-red-500" />
                  <span className="text-sm text-gray-600">
                    {leads.filter(l => l.score_grade === 'hot').length} Hot Leads
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-gray-600">
                    Avg Score: {leads.length > 0 ? (leads.reduce((sum, l) => sum + l.score, 0) / leads.length).toFixed(0) : '0'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6 border-b border-gray-200">
        <div className="flex gap-6">
          <button
            onClick={() => setActiveTab('leads')}
            className={`pb-3 px-1 border-b-2 font-medium transition-colors ${
              activeTab === 'leads' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500'
            }`}
          >
            Scored Leads ({leads.length})
          </button>
          <button
            onClick={() => setActiveTab('model')}
            className={`pb-3 px-1 border-b-2 font-medium transition-colors ${
              activeTab === 'model' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500'
            }`}
          >
            Model Performance
          </button>
        </div>
      </div>

      {activeTab === 'leads' && (
        <>
          {leads.length === 0 ? (
            <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-12 text-center">
              <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Leads Yet</h3>
              <p className="text-gray-600 mb-4">
                Start scoring leads by capturing them from your website, ads, or manual entry.
              </p>
              <button 
                onClick={() => alert('Lead creation is coming soon! This page currently displays leads captured from your marketing channels with AI-powered scoring.')}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Add First Lead
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {leads.map((lead) => (
                <div key={lead.id} className="bg-white rounded-xl border-2 border-gray-200 p-6 hover:border-blue-300 transition-all">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-semibold text-gray-900">{lead.contact_name}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium border-2 ${getPriorityColor(lead.score_grade)}`}>
                          <div className="flex items-center gap-1">
                            {getPriorityIcon(lead.score_grade)}
                            {lead.score_grade.toUpperCase()}
                          </div>
                        </span>
                        <span className="text-2xl font-bold text-blue-600">{lead.score}</span>
                        {getStatusBadge(lead.status)}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600 flex-wrap">
                        {lead.contact_email && (
                          <div className="flex items-center gap-1">
                            <Mail className="w-4 h-4" />
                            {lead.contact_email}
                          </div>
                        )}
                        {lead.contact_phone && (
                          <div className="flex items-center gap-1">
                            <Phone className="w-4 h-4" />
                            {lead.contact_phone}
                          </div>
                        )}
                        {lead.company_name && (
                          <div className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {lead.company_name}
                          </div>
                        )}
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {formatTimeAgo(lead.created_at)}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <select
                        value={lead.status}
                        onChange={(e) => updateLeadStatus(lead.id, e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="qualified">Qualified</option>
                        <option value="converted">Converted</option>
                        <option value="lost">Lost</option>
                      </select>
                      <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2">
                        <Phone className="w-4 h-4" />
                        Contact
                      </button>
                    </div>
                  </div>

                  {lead.source && (
                    <div className="mb-4">
                      <p className="text-xs text-gray-500 mb-1">Lead Source</p>
                      <p className="text-sm font-medium text-gray-700">{lead.source}</p>
                    </div>
                  )}

                  {Object.keys(lead.score_breakdown).length > 0 && (
                    <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-xs font-medium text-blue-900 mb-2">SCORE BREAKDOWN</p>
                      <div className="grid grid-cols-4 gap-3">
                        {Object.entries(lead.score_breakdown).map(([key, value]) => (
                          <div key={key}>
                            <p className="text-xs text-blue-600">{key.replace(/_/g, ' ')}</p>
                            <p className="text-sm font-semibold text-blue-900">+{value}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {lead.recommended_actions.length > 0 && (
                    <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-1">RECOMMENDED ACTIONS</p>
                        <div className="flex flex-wrap gap-2">
                          {lead.recommended_actions.map((action, idx) => (
                            <span key={idx} className="px-3 py-1 bg-green-50 text-green-700 rounded text-xs font-medium">
                              {action}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {activeTab === 'model' && (
        <div className="grid grid-cols-3 gap-6">
          {calculateMetrics().map((metric, index) => (
            <div key={index} className="bg-white rounded-xl border-2 border-gray-200 p-6">
              <p className="text-sm font-medium text-gray-600 mb-2">{metric.label}</p>
              <p className="text-3xl font-bold text-gray-900 mb-1">{metric.value}</p>
              <p className="text-sm text-gray-600">{metric.change}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
